import { type ApolloClient, gql } from '@apollo/client/core';
import type { EventArtist as GQLEventArtist, Mutation, Query } from '../../../generated/api.graphql';
import type { VPResult } from '../../VPResult';
import * as EventArtist from './EventArtist';
import * as EventArtistRider from './EventArtistRider';
import { EVENT_ARTIST_RIDER_FIELDS } from './fragments';
import { take } from '../../../util';

// `onStage` / `offStage` sit here on the event artist, not in `EVENT_ARTIST_RIDER_FIELDS`: they
// are one pair per event artist, shared by both of its riders. Putting them in the fragment would
// not resolve, and would leak them into the save payload's rider selection too.
export const EVENT_ARTIST_RIDERS_QUERY = gql`
  query GetEventArtistRiders($eventId: Int!) {
    eventArtists(eventId: $eventId) {
      id
      onStage
      offStage
      artist {
        id
        name
      }
      riders {
        ...EventArtistRiderFields
      }
    }
  }
  ${EVENT_ARTIST_RIDER_FIELDS}
`;

type EventArtistRidersParams = {
  eventId: number;
  artistId?: number;
};

/**
 * The AppSchema root field takes only `eventId` — the legacy nested
 * `event(id:) { eventArtists(artistId:) }` argument is gone — so narrowing to one artist happens
 * here rather than on the server. Callers that pass an `artistId` still get at most one row.
 */
export const fetchEventArtistRiders = async (client: ApolloClient<Query>, { eventId, artistId }: EventArtistRidersParams): Promise<EventArtist.EventArtist[]> => {
  const { data } = await client.query({
    query: EVENT_ARTIST_RIDERS_QUERY,
    variables: {
      eventId,
    },
    fetchPolicy: 'network-only',
  });

  const eventArtists: GQLEventArtist[] = data.eventArtists ?? [];

  return EventArtist.fromGqlCollection(
    artistId === undefined ? eventArtists : eventArtists.filter(eventArtist => eventArtist.artist?.id === artistId),
  );
};

export const SAVE_EVENT_ARTIST_RIDER_MUTATION = gql`
  mutation SaveEventArtistRider($input: SaveEventArtistRiderMutationInput!) {
    saveEventArtistRider(input: $input) {
      rider {
        ...EventArtistRiderFields
      }
      errors
      success
    }
  }
  ${EVENT_ARTIST_RIDER_FIELDS}
`;

// `riderType` is optional to match both the schema's nullable `EventArtistRiderInput.riderType`
// and the store, which builds this payload with a `take` over an all-optional domain type.
type EventArtistRiderInput = {
  id?: number;
  notes?: string;
  riderType?: EventArtistRider.EventArtistRiderType;
  artistLoadInTime?: string | null;
  stageSoundCheckTime?: string | null;
  soundCheckType?: string | null;
  stagePerformersNumber?: number | null;
  backstagePassesNumber?: number | null;
  attachments?: {
    id: number;
    _destroy?: boolean;
  }[];
};

type SaveEventArtistRiderParams = EventArtist.SetTimes & {
  eventId: number;
  artistId: number;
  rider: EventArtistRiderInput;
};

// `errors` is a JSON scalar here, not the `[String!]` the legacy payload carried — the shape is
// whatever the backend puts in it, so don't promise callers an array.
export const saveEventArtistRider = async (client: ApolloClient<Mutation>, { eventId, artistId, rider, onStage, offStage }: SaveEventArtistRiderParams): Promise<VPResult<EventArtistRider.EventArtistRider, unknown>> => {
  const { data } = await client.mutate({
    mutation: SAVE_EVENT_ARTIST_RIDER_MUTATION,
    variables: {
      // The set times are the event artist's own columns, so they ride beside `rider`, not inside
      // it — the backend writes them only once the rider save succeeded. `take` drops the ones the
      // caller did not pass, which is what keeps a rider-only save sending the input it always
      // sent; the backend reads a missing arg as "leave this alone", and an explicit null as a clear.
      input: {
        eventId,
        artistId,
        rider,
        ...take<EventArtist.SetTimes>({ onStage, offStage }, 'onStage', 'offStage'),
      },
    },
    update: cache => {
      // The riders hang off the root `eventArtists` field now, not the legacy `app` wrapper.
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'eventArtists' });
      cache.gc();
    },
  });

  const response = data.saveEventArtistRider;
  // The payload itself is nullable. Say so, rather than letting the error branch below throw an
  // opaque TypeError on a transport-level failure.
  if (!response) {
    throw new Error('saveEventArtistRider returned no payload');
  }

  // Branch on `success` alone: `errors` is a JSON scalar the backend sends as a present-but-empty
  // value on success, so an `if (errors)` guard would report every save as a failure.
  if (response.success) {
    // `rider` is nullable even here. Falling through with `undefined` would let
    // `saveRiderAttachment` read no id back and create a second rider for the document.
    if (!response.rider) {
      throw new Error('saveEventArtistRider reported success without a rider');
    }

    return {
      success: true,
      data: EventArtistRider.fromGql(response.rider),
    };
  }

  return {
    success: false,
    errors: response.errors,
  };
};

export const TRIGGER_ARTIST_ZAPIER_MUTATION = gql`
  mutation TriggerArtistZapier($eventArtistId: Int!) {
    triggerArtistZapier(input: { eventArtistId: $eventArtistId }) {
      success
    }
  }
`;

/**
 * Pushes an event artist to Zapier — the AppSchema replacement for the legacy artist row's
 * `POST /venue_app/event_artists/:id/send_to_zapier`.
 *
 * `eventArtistId` is the **legacy** event artist id (`EventArtist.legacyEventArtistId`), not the
 * AppSchema `EventArtist.id`: the mutation resolves `VenueApp::EventArtist`.
 *
 * Returns a bare boolean rather than a `VPResult` because the payload carries no `errors` and no
 * entity — a `VPResult` here would only hand callers an empty error list to unwrap.
 *
 * The two failure modes are deliberately distinct. `success: false` means the dispatch raised
 * server-side and the backend rescued it and reported to Sentry itself, so it comes back as
 * `false` for the caller to surface. Everything else throws: the mutation's own refusals
 * (`UnauthorizedError` / `NotFoundError`) reject `mutate` outright under Apollo's default
 * `errorPolicy: 'none'`, and the null-payload guard below covers the residue — the payload is
 * `Maybe<…>` in the schema. Coercing either to `false` would tell the user their push failed when
 * it was never attempted.
 */
export const triggerArtistZapier = async (client: ApolloClient<Mutation>, eventArtistId: number): Promise<boolean> => {
  const { data } = await client.mutate({
    mutation: TRIGGER_ARTIST_ZAPIER_MUTATION,
    variables: {
      eventArtistId,
    },
  });

  const response = data.triggerArtistZapier;
  if (!response) {
    throw new Error('triggerArtistZapier returned no payload');
  }

  return response.success;
};
